import {
  CONFIRMABLE_MIN_LEVEL,
  CONFIRM_UNDO_WINDOW_MS,
  COVERAGE_MIN_LEVEL,
  COVERAGE_RISK_MAX,
  DEFAULT_LANG,
  FOCUS_MAX,
  JOURNAL_MAX_LEN,
  JOURNAL_MAX_PER_PERIOD,
  L5_GOAL,
  LANGS,
  LEAD_LEVEL,
  LEVELS,
  NOTIFICATION_KINDS,
  NOTIFY_HOUR_DEFAULT,
  NUDGE_DAYS_BEFORE_END,
  RANKS,
  REF_SCORE,
  REMIND_DAYS_BEFORE_END_DEFAULT,
  ROLES,
  TASK_NOTE_EXCERPT_LENGTH,
  TASK_NOTE_MAX_LENGTH,
  TASK_NOTE_MIN_LENGTH,
  TEAM_TIME_ZONE_DEFAULT,
  UNCONFIRMED_COUNTS_AS
} from "./chunk-4GBNA777.js";

// src/errors.ts
var ERROR_CODES = [
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "VALIDATION_FAILED",
  "CSRF_HEADER_MISSING",
  "RATE_LIMITED",
  "CONFLICT",
  "PAYLOAD_TOO_LARGE",
  "INTERNAL",
  // Domain rules
  "FOCUS_LIMIT_REACHED",
  "SELF_CONFIRM_FORBIDDEN",
  "TARGET_ROLE_FORBIDDEN",
  "UNDO_WINDOW_EXPIRED",
  "CONFIRM_LEVEL_INVALID",
  "AREA_UNKNOWN",
  "AREA_NOT_OPTIONAL",
  "LAST_LEADER",
  "SELF_MANAGE_FORBIDDEN",
  "EMAIL_DOMAIN_NOT_ALLOWED",
  "EMAIL_TAKEN",
  "NO_OPEN_PERIOD",
  "PERIOD_NOT_CURRENT",
  "JOURNAL_LIMIT_REACHED",
  "TIME_ZONE_INVALID"
];
function isApiErrorBody(value) {
  return typeof value === "object" && value !== null && "code" in value && typeof value.code === "string" && ERROR_CODES.includes(value.code);
}

// src/auth/permissions.ts
var PERMISSIONS = [
  "self.assess",
  "team.coverage",
  "team.experts",
  "team.view",
  "team.confirm",
  "team.manage",
  "period.manage",
  "audit.view"
];
var BY_ROLE = {
  member: ["self.assess", "team.coverage", "team.experts"],
  sub_leader: ["self.assess", "team.coverage", "team.experts", "team.view", "team.confirm"],
  leader: [
    "self.assess",
    "team.coverage",
    "team.experts",
    "team.view",
    "team.confirm",
    "team.manage",
    "period.manage",
    "audit.view"
  ]
};
function permissionsFor(role) {
  return BY_ROLE[role];
}
function hasPermission(role, permission) {
  return BY_ROLE[role].includes(permission);
}
function levelsNeedConfirmation(role) {
  return role !== "leader";
}
function checkCanConfirm(actor, target) {
  if (!hasPermission(actor.role, "team.confirm")) return { ok: false, code: "FORBIDDEN" };
  if (actor.id === target.id) return { ok: false, code: "SELF_CONFIRM_FORBIDDEN" };
  if (!levelsNeedConfirmation(target.role)) return { ok: false, code: "TARGET_ROLE_FORBIDDEN" };
  if (actor.role === "sub_leader" && target.role !== "member")
    return { ok: false, code: "TARGET_ROLE_FORBIDDEN" };
  return { ok: true };
}

// src/scoring/effective-level.ts
function effectiveLevel(selfLevel, confirmedLevel) {
  if (selfLevel < CONFIRMABLE_MIN_LEVEL) return selfLevel;
  if (confirmedLevel === null) return UNCONFIRMED_COUNTS_AS;
  return Math.min(selfLevel, confirmedLevel);
}
function isPendingConfirmation(selfLevel, confirmedLevel) {
  if (selfLevel === null || selfLevel < CONFIRMABLE_MIN_LEVEL) return false;
  return confirmedLevel === null || confirmedLevel < selfLevel;
}

// src/scoring/status.ts
function areaStatus(min, level, isNa) {
  if (isNa) return "na";
  if (level === null) return "unassessed";
  if (level < min) return "gap";
  return level > min ? "up" : "ok";
}
function rankStanding(score, rank) {
  if (score < REF_SCORE[rank]) return "below";
  const next = RANKS[RANKS.indexOf(rank) + 1];
  if (next !== void 0 && score >= REF_SCORE[next]) return "above";
  return "meets";
}

// src/scoring/score.ts
function pointsOf(level) {
  return level === null ? 0 : level - 1;
}
function toScore(points, applicable) {
  if (applicable === 0) return 0;
  return Math.round(points / (4 * applicable) * 100);
}
function computeScores(structure, states, rank, role = "member") {
  const areas = [];
  const groups = [];
  const confirmedBySomeone = levelsNeedConfirmation(role);
  for (const group of structure.groups) {
    let applicable = 0;
    let assessed = 0;
    let selfPoints = 0;
    let effectivePoints = 0;
    let minPoints = 0;
    let okCount = 0;
    for (const area of group.areas) {
      const state = states[area.id];
      const min = area.mins[rank];
      const isNa = area.optional && state?.isNa === true;
      const selfLevel = isNa ? null : state?.selfLevel ?? null;
      const confirmedLevel = confirmedBySomeone ? state?.confirmedLevel ?? null : selfLevel;
      const effective = selfLevel === null ? null : effectiveLevel(selfLevel, confirmedLevel);
      const result = {
        areaId: area.id,
        groupId: group.id,
        min,
        optional: area.optional,
        isNa,
        selfLevel,
        confirmedLevel,
        effectiveLevel: effective,
        pending: isPendingConfirmation(selfLevel, confirmedLevel),
        selfStatus: areaStatus(min, selfLevel, isNa),
        effectiveStatus: areaStatus(min, effective, isNa)
      };
      areas.push(result);
      if (isNa) continue;
      applicable += 1;
      minPoints += pointsOf(min);
      selfPoints += pointsOf(selfLevel);
      effectivePoints += pointsOf(effective);
      if (selfLevel !== null) assessed += 1;
      if (effective !== null && effective >= min) okCount += 1;
    }
    groups.push({
      groupId: group.id,
      applicable,
      assessed,
      selfScore: toScore(selfPoints, applicable),
      effectiveScore: toScore(effectivePoints, applicable),
      minScore: toScore(minPoints, applicable),
      okCount
    });
  }
  const applicableAreas = areas.filter((a) => !a.isNa);
  const distribution = Object.fromEntries(LEVELS.map((l) => [l, 0]));
  for (const a of applicableAreas) if (a.effectiveLevel !== null) distribution[a.effectiveLevel] += 1;
  return {
    selfScore: toScore(
      applicableAreas.reduce((sum, a) => sum + pointsOf(a.selfLevel), 0),
      applicableAreas.length
    ),
    effectiveScore: toScore(
      applicableAreas.reduce((sum, a) => sum + pointsOf(a.effectiveLevel), 0),
      applicableAreas.length
    ),
    total: areas.length,
    applicable: applicableAreas.length,
    assessed: applicableAreas.filter((a) => a.selfLevel !== null).length,
    areas,
    groups,
    gaps: applicableAreas.filter((a) => a.effectiveLevel === null || a.effectiveLevel < a.min),
    pending: applicableAreas.filter((a) => a.pending),
    l5Count: applicableAreas.filter((a) => a.effectiveLevel === 5).length,
    l5Goal: L5_GOAL[rank],
    distribution
  };
}
function referenceScore(structure, rank) {
  const mins = structure.groups.flatMap((g) => g.areas.map((a) => a.mins[rank])).sort((a, b) => b - a);
  const raised = mins.map((min, i) => i < L5_GOAL[rank] ? 5 : min);
  const points = raised.reduce((sum, level) => sum + level - 1, 0);
  return points / (4 * mins.length) * 100;
}

// src/scoring/periods.ts
var DAY_MS = 24 * 60 * 60 * 1e3;
var utc = (date) => /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
var iso = (date) => date.toISOString().slice(0, 10);
var overAt = (endsAt) => utc(endsAt).getTime() + DAY_MS;
function wallClock(now, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
  }).formatToParts(now);
  const part = (type) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
}
function localDate(now, timeZone) {
  return iso(new Date(wallClock(now, timeZone)));
}
function isValidPeriodRange({ startsAt, endsAt }) {
  return endsAt >= startsAt;
}
function daysLeftInPeriod(endsAt, now, timeZone) {
  return Math.max(0, Math.ceil((overAt(endsAt) - wallClock(now, timeZone)) / DAY_MS));
}
function daysPastEnd(endsAt, now, timeZone) {
  const late = wallClock(now, timeZone) - overAt(endsAt);
  return late < 0 ? 0 : Math.floor(late / DAY_MS) + 1;
}
function periodElapsed({ startsAt, endsAt }, now, timeZone) {
  const start = utc(startsAt).getTime();
  return Math.min(1, Math.max(0, (wallClock(now, timeZone) - start) / (overAt(endsAt) - start)));
}
function monthOf(date) {
  const [y = 0, m = 1] = date.split("-").map(Number);
  return { startsAt: iso(new Date(Date.UTC(y, m - 1, 1))), endsAt: iso(new Date(Date.UTC(y, m, 0))) };
}
function monthAround(now, timeZone) {
  return monthOf(localDate(now, timeZone));
}
function formatPeriodLabel({ startsAt, endsAt }, lang) {
  const range = `${formatPeriodDate(startsAt, lang)} \u2013 ${formatPeriodDate(endsAt, lang)}`;
  return lang === "vi" ? `K\u1EF3 ${range}` : range;
}
function formatPeriodDate(date, lang) {
  if (lang === "vi") return date.split("-").reverse().join("/");
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  }).format(utc(date));
}
function formatPeriodShort({ startsAt, endsAt }) {
  const [sy = "", sm = "", sd = ""] = startsAt.split("-");
  const [ey = "", em = "", ed = ""] = endsAt.split("-");
  return sy === ey ? `${sd}/${sm}\u2013${ed}/${em}/${ey.slice(2)}` : `${sd}/${sm}/${sy.slice(2)}\u2013${ed}/${em}/${ey.slice(2)}`;
}
function canUndoConfirmation(createdAt, now) {
  const elapsed = now.getTime() - createdAt.getTime();
  return elapsed >= 0 && elapsed <= CONFIRM_UNDO_WINDOW_MS;
}

// src/scoring/review.ts
function reviewCounts(areas, reviewedIds) {
  const applicable = areas.filter((a) => !a.isNa);
  return {
    reviewed: applicable.filter((a) => reviewedIds.has(a.areaId)).length,
    applicable: applicable.length
  };
}
function reviewOrder(areas, reviewedIds) {
  return {
    todo: areas.filter((a) => !reviewedIds.has(a.areaId)),
    done: areas.filter((a) => reviewedIds.has(a.areaId))
  };
}
function leftToReview(areas, reviewedIds) {
  return areas.filter((a) => (a.selfLevel !== null || a.isNa) && !reviewedIds.has(a.areaId)).map((a) => a.areaId);
}
function needsQuickReview(areas, reviewedIds) {
  return leftToReview(areas, reviewedIds).length > 0;
}

// src/scoring/period-diff.ts
function toSnapshotAreas(areas) {
  const out = {};
  for (const a of areas) {
    if (a.selfLevel === null && a.confirmedLevel === null && !a.isNa) continue;
    out[a.areaId] = { s: a.selfLevel, c: a.confirmedLevel, na: a.isNa };
  }
  return out;
}
function fromSnapshotAreas(areas) {
  return Object.fromEntries(
    Object.entries(areas).map(([id, a]) => [id, { selfLevel: a.s, confirmedLevel: a.c, isNa: a.na }])
  );
}
function diffAreas(structure, before, after) {
  const raised = [];
  const lowered = [];
  for (const group of structure.groups) {
    for (const area of group.areas) {
      const prev = before[area.id];
      const next = after[area.id];
      if (area.optional && (prev?.isNa === true || next?.isNa === true)) continue;
      const from = prev?.selfLevel ?? null;
      const to = next?.selfLevel ?? null;
      const change = { areaId: area.id, groupId: group.id, from, to };
      if (to !== null && (from === null || to > from)) raised.push(change);
      else if (from !== null && (to === null || to < from)) lowered.push(change);
    }
  }
  return { raised, lowered };
}
function focusRaised(levelAtPick, levelAtEnd) {
  return levelAtEnd !== null && levelAtEnd > (levelAtPick ?? 1);
}
function scoreChange(before, now) {
  return {
    self: now.selfScore - before.selfScore,
    effective: now.effectiveScore - before.effectiveScore,
    standingBefore: rankStanding(before.effectiveScore, before.rank),
    standingNow: rankStanding(now.effectiveScore, now.rank)
  };
}

// src/scoring/period-stats.ts
function isFullyReviewed(m) {
  return m.applicable > 0 && m.reviewed >= m.applicable;
}
function periodStats(members) {
  const active = members.filter((m) => m.status === "active");
  return {
    people: active.length,
    fullyAssessed: active.filter((m) => m.applicable > 0 && m.assessed >= m.applicable).length,
    fullyReviewed: active.filter(isFullyReviewed).length,
    pending: active.reduce((n, m) => n + m.pendingCount, 0),
    confirmationsUsed: active.reduce((n, m) => n + m.confirmationsUsed, 0),
    withFocus: active.filter((m) => m.focusAreaIds.length > 0).length
  };
}

// src/scoring/coverage.ts
function teamCoverage(people, areaIds) {
  return areaIds.map((areaId) => {
    const areas = people.map((p) => p.find((a) => a.areaId === areaId));
    const count = areas.filter((a) => a !== void 0 && canHelpIn(a)).length;
    const leads = areas.filter((a) => a !== void 0 && leadsIn(a)).length;
    const applicableFor = areas.filter((a) => a?.isNa !== true).length;
    return { areaId, count, leads, applicableFor, risk: applicableFor > 0 && count <= COVERAGE_RISK_MAX };
  });
}
function leadsIn(area) {
  return !area.isNa && area.confirmedLevel === LEAD_LEVEL;
}
function claimsLead(area) {
  return !area.isNa && area.selfLevel === LEAD_LEVEL && area.confirmedLevel !== LEAD_LEVEL;
}
function coverageNudge(area, coverage) {
  if (area.isNa) return null;
  if (canHelpIn(area)) return "covered";
  if (!coverage.risk || area.selfLevel === null) return null;
  if (area.selfLevel >= COVERAGE_MIN_LEVEL) return "pending";
  return area.selfLevel === COVERAGE_MIN_LEVEL - 1 ? "next" : null;
}
function canHelpIn(area) {
  return !area.isNa && area.confirmedLevel !== null && area.confirmedLevel >= COVERAGE_MIN_LEVEL;
}
function helpAreas(areas, focusIds) {
  const focus = new Set(focusIds);
  return areas.filter((a) => !a.isNa && (a.selfStatus === "gap" || focus.has(a.areaId))).map((a) => a.areaId);
}

// src/scoring/reminders.ts
var DAY_MS2 = 24 * 60 * 60 * 1e3;
function localClock(now, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    weekday: "short"
  }).formatToParts(now);
  const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(String(v.weekday)) + 1;
  return { date: `${String(v.year)}-${String(v.month)}-${String(v.day)}`, hour: Number(v.hour), weekday };
}
function midpointDate({ startsAt, endsAt }) {
  const start = Date.parse(`${startsAt}T00:00:00Z`);
  const end = Date.parse(`${endsAt}T00:00:00Z`);
  return new Date(start + Math.floor((end - start) / DAY_MS2 / 2) * DAY_MS2).toISOString().slice(0, 10);
}
function isoWeek(date) {
  const d = /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
  const weekday = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - weekday);
  const year = d.getUTCFullYear();
  const week = Math.ceil(((d.getTime() - Date.UTC(year, 0, 1)) / DAY_MS2 + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
}
function dueReminders(period, now, timeZone, settings) {
  if (!period) return [];
  const clock = localClock(now, timeZone);
  if (clock.hour < settings.hour) return [];
  const left = daysLeftInPeriod(period.endsAt, now, timeZone);
  const late = daysPastEnd(period.endsAt, now, timeZone);
  const days = (Date.parse(period.endsAt) - Date.parse(period.startsAt)) / DAY_MS2 + 1;
  const due = [];
  if (late === 0 && days >= 3 && clock.date === midpointDate(period))
    due.push({ kind: "period.midpoint", bucket: period.id });
  if (late === 0 && settings.remindDaysBeforeEnd > 0 && left === settings.remindDaysBeforeEnd)
    due.push({ kind: "period.ending", bucket: period.id });
  if (late > 0 && (late - 1) % 7 === 0) due.push({ kind: "period.overdue", bucket: `${period.id}:${late}` });
  if (late === 0 && clock.weekday === 1) due.push({ kind: "digest.weekly", bucket: isoWeek(clock.date) });
  if (late === 0 && left === NUDGE_DAYS_BEFORE_END) due.push({ kind: "member.nudge", bucket: period.id });
  return due;
}
export {
  CONFIRMABLE_MIN_LEVEL,
  CONFIRM_UNDO_WINDOW_MS,
  COVERAGE_MIN_LEVEL,
  COVERAGE_RISK_MAX,
  DEFAULT_LANG,
  ERROR_CODES,
  FOCUS_MAX,
  JOURNAL_MAX_LEN,
  JOURNAL_MAX_PER_PERIOD,
  L5_GOAL,
  LANGS,
  LEAD_LEVEL,
  LEVELS,
  NOTIFICATION_KINDS,
  NOTIFY_HOUR_DEFAULT,
  NUDGE_DAYS_BEFORE_END,
  PERMISSIONS,
  RANKS,
  REF_SCORE,
  REMIND_DAYS_BEFORE_END_DEFAULT,
  ROLES,
  TASK_NOTE_EXCERPT_LENGTH,
  TASK_NOTE_MAX_LENGTH,
  TASK_NOTE_MIN_LENGTH,
  TEAM_TIME_ZONE_DEFAULT,
  UNCONFIRMED_COUNTS_AS,
  areaStatus,
  canHelpIn,
  canUndoConfirmation,
  checkCanConfirm,
  claimsLead,
  computeScores,
  coverageNudge,
  daysLeftInPeriod,
  daysPastEnd,
  diffAreas,
  dueReminders,
  effectiveLevel,
  focusRaised,
  formatPeriodDate,
  formatPeriodLabel,
  formatPeriodShort,
  fromSnapshotAreas,
  hasPermission,
  helpAreas,
  isApiErrorBody,
  isFullyReviewed,
  isPendingConfirmation,
  isValidPeriodRange,
  isoWeek,
  leadsIn,
  leftToReview,
  levelsNeedConfirmation,
  localClock,
  localDate,
  midpointDate,
  monthAround,
  monthOf,
  needsQuickReview,
  periodElapsed,
  periodStats,
  permissionsFor,
  pointsOf,
  rankStanding,
  referenceScore,
  reviewCounts,
  reviewOrder,
  scoreChange,
  teamCoverage,
  toScore,
  toSnapshotAreas
};
//# sourceMappingURL=index.js.map